import React, { useState, useRef, useEffect } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { ModalCadastro } from '../../../../components/Modais';
import DocumentScanner from '../../../../components/DocumentScanner';
import { BotaoPadrao } from '../../../../components/Botoes';
import { mostrarToast } from '../../../../components/Toasts';
import { post, postComProgresso } from '../../../../util/AcessoApi';
import { removerAcentos } from '../../../../util/Utilitarios';
import { MODULO } from '../../../../util/Enums';
import '../../../../assets/css/upload-component.css';
import '../../../../assets/css/upload-guia.css';
import '../../../../assets/css/icones/iconsmind/style.css';

/**
 * Extração do documento da guia — aberta pelo botão no header do cadastro.
 *
 * Usar o documento é opcional: quem quiser digitar tudo à mão simplesmente não
 * abre este modal. Aqui o usuário adiciona um ou mais arquivos (fotos/PDF do
 * comprovante) e marca, por arquivo, o que deve ser lido pela extração e o que
 * deve ficar anexado à guia. Ao confirmar:
 *  1. cada arquivo é enviado ao backend, que grava no S3 com as credenciais do
 *     servidor (mesmo desenho do hub — o navegador não fala com o S3);
 *  2. os marcados como "Extrair" são lidos pela extração no backend;
 *  3. o cadastro recebe os campos lidos e os anexos já prontos.
 *
 * Nada é gravado no banco aqui: a guia só é criada quando o cadastro é salvo.
 *
 * Serve as duas telas de guia. A leitura do documento é a mesma nas duas — o
 * que muda é o casamento com o cadastro, e disso quem cuida é o backend.
 *
 * Props:
 *  - abrirModal / setAbrirModal: controle do modal
 *  - onConcluir(extracao, arquivos): extracao é null quando não houve leitura
 *  - particular: true na guia particular. Vai para a extração, que então não
 *    procura convênio nem domínios TISS e tira o contrato do estabelecimento.
 *  - modulo: módulo dos anexos; sem ele, o da guia de convênio.
 */

const DIRETORIO_S3 = 'guia/DDMMYYYY/';
const TAMANHO_MAXIMO_MB = 10;
const TAMANHO_MAXIMO_BYTES = TAMANHO_MAXIMO_MB * 1024 * 1024;
const TIPOS_ACEITOS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const EXTENSOES_ACEITAS = '.jpg,.jpeg,.png,.webp,.pdf';
// Os avisos da extração são frases longas; o padrão do toast (5s) é curto para elas.
const TEMPO_AVISO_MS = 8000;

const estiloModal = {
    content: {
        left: '50%',
        top: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: '760px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        overflow: 'auto'
    }
};

const gerarId = () => 'files-' + Math.random().toString().replace('.', '');

const ehImagem = (arquivo) => ((arquivo && arquivo.type) || '').toLowerCase().indexOf('image/') === 0;

const obterExtensao = (nome) => {
    const partes = (nome || '').split('.');
    return partes.length > 1 ? partes[partes.length - 1].toLowerCase() : '';
};

const tamanhoLegivel = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const iconePorExtensao = (extensao) => {
    if (extensao === 'pdf') return 'icon-File-ClipboardFileText';
    if (['jpg', 'jpeg', 'png', 'webp'].indexOf(extensao) > -1) return 'icon-File-Pictures';
    return 'icon-File-Upload';
};

const ModalExtracaoDocumento = (props) => {
    const inputRef = useRef(null);
    const [documentos, setDocumentos] = useState([]);
    const [processando, setProcessando] = useState(false);
    // '' | 'enviando' | 'lendo' — dirige a barra de progresso e o texto exibido.
    const [fase, setFase] = useState('');
    const [arrastando, setArrastando] = useState(false);

    // Edição de imagem (scanner): as imagens recém-adicionadas entram numa fila e o
    // scanner abre uma a uma; clicar na miniatura reabre o editor daquele documento.
    const [filaEdicao, setFilaEdicao] = useState([]);
    const [editandoId, setEditandoId] = useState(null);
    // O ModalCadastro registra o listener de Escape uma unica vez (closure do
    // primeiro render), entao o fechamento le o estado atual por ref.
    const editandoIdRef = useRef(null);
    editandoIdRef.current = editandoId;
    // Mesmo motivo: o Escape não pode fechar o modal no meio de um envio.
    const processandoRef = useRef(false);
    processandoRef.current = processando;

    // Libera as URLs de pré-visualização ao desmontar.
    useEffect(() => {
        return () => {
            documentos.forEach(documento => {
                if (documento.previewUrl) URL.revokeObjectURL(documento.previewUrl);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const limpar = () => {
        // Forma funcional: o Escape do ModalCadastro chama daqui por uma closure
        // antiga, e a lista precisa ser a atual para as URLs serem liberadas.
        setDocumentos(atuais => {
            atuais.forEach(documento => {
                if (documento.previewUrl) URL.revokeObjectURL(documento.previewUrl);
            });
            return [];
        });
        setFilaEdicao([]);
        setEditandoId(null);
        setFase('');
    };

    /** Fecha o modal e descarta o que estava na tela (nada foi enviado ainda). */
    const fechar = () => {
        if (processandoRef.current) return;
        limpar();
        props.setAbrirModal(false);
    };

    const adicionarArquivos = (arquivos) => {
        const novos = [];

        for (let index = 0; index < arquivos.length; index++) {
            const arquivo = arquivos[index];
            const tipo = (arquivo.type || '').toLowerCase();

            if (TIPOS_ACEITOS.indexOf(tipo) === -1) {
                mostrarToast('erro', `O arquivo "${arquivo.name}" não é um tipo aceito (JPG, PNG, WebP ou PDF).`);
                continue;
            }

            if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
                mostrarToast('erro', `O arquivo "${arquivo.name}" excede ${TAMANHO_MAXIMO_MB}MB.`);
                continue;
            }

            novos.push({
                id: gerarId(),
                arquivo,
                // A foto original é preservada: reabrir o editor sempre parte dela,
                // então recortes sucessivos não vão degradando a imagem.
                original: arquivo,
                previewUrl: tipo.indexOf('image/') === 0 ? URL.createObjectURL(arquivo) : null,
                extensao: obterExtensao(arquivo.name),
                extrair: true,
                anexar: true,
                percentual: 0
            });
        }

        if (novos.length > 0) {
            setDocumentos(atuais => [...atuais, ...novos]);

            const imagens = novos.filter(documento => ehImagem(documento.arquivo)).map(documento => documento.id);
            if (imagens.length > 0)
                setFilaEdicao(atuais => [...atuais, ...imagens]);
        }
    };

    const selecionarArquivos = (evento) => {
        adicionarArquivos(Array.prototype.slice.call(evento.target.files || []));
        if (inputRef.current) inputRef.current.value = '';
    };

    const soltarArquivos = (evento) => {
        evento.preventDefault();
        setArrastando(false);
        if (processando) return;
        adicionarArquivos(Array.prototype.slice.call(evento.dataTransfer.files || []));
    };

    const alternarMarcacao = (id, campo) => {
        setDocumentos(atuais => atuais.map(documento =>
            documento.id === id ? { ...documento, [campo]: !documento[campo] } : documento));
    };

    const removerDocumento = (id) => {
        setDocumentos(atuais => {
            const alvo = atuais.find(documento => documento.id === id);
            if (alvo && alvo.previewUrl) URL.revokeObjectURL(alvo.previewUrl);
            return atuais.filter(documento => documento.id !== id);
        });

        setFilaEdicao(atuais => atuais.filter(item => item !== id));
        if (editandoId === id) setEditandoId(null);
    };

    const atualizarPercentual = (id, percentual) => {
        setDocumentos(atuais => atuais.map(documento =>
            documento.id === id ? { ...documento, percentual } : documento));
    };

    // Documento aberto no editor: a reedicao (clique na miniatura) tem prioridade
    // sobre a fila das imagens recem-adicionadas.
    const idEmEdicao = editandoId || filaEdicao[0] || null;
    const documentoEmEdicao = idEmEdicao ? documentos.find(documento => documento.id === idEmEdicao) || null : null;

    const abrirEdicao = (documento) => {
        if (processando || !ehImagem(documento.arquivo)) return;
        setEditandoId(documento.id);
    };

    /** Fecha o editor: encerra a reedicao ou avanca a fila das imagens novas. */
    const fecharEdicao = () => {
        if (editandoIdRef.current) {
            setEditandoId(null);
            return;
        }
        setFilaEdicao(atuais => atuais.slice(1));
    };

    /**
     * Recebe do scanner a imagem já tratada (recorte de perspectiva, rotação e/ou
     * realce) e a coloca no lugar do arquivo do documento. O scanner fecha em
     * seguida por conta própria (onClose), avançando a fila.
     */
    const confirmarEdicao = (arquivoEditado) => {
        if (!documentoEmEdicao) return;

        if (arquivoEditado.size > TAMANHO_MAXIMO_BYTES) {
            mostrarToast('erro', 'A imagem editada ficou com mais de ' + TAMANHO_MAXIMO_MB + 'MB. Mantido o original.');
            return;
        }

        const id = documentoEmEdicao.id;
        setDocumentos(atuais => atuais.map(item => {
            if (item.id !== id) return item;
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            return {
                ...item,
                arquivo: arquivoEditado,
                extensao: obterExtensao(arquivoEditado.name),
                previewUrl: URL.createObjectURL(arquivoEditado)
            };
        }));
    };

    /** Lê o arquivo como base64 (sem o prefixo "data:...;base64,"). */
    const lerComoBase64 = (arquivo) => new Promise((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => {
            const resultado = (leitor.result || '').toString();
            const separador = resultado.indexOf(',');
            resolve(separador > -1 ? resultado.substring(separador + 1) : resultado);
        };
        leitor.onerror = () => reject(new Error('Não foi possível ler o arquivo "' + arquivo.name + '".'));
        leitor.readAsDataURL(arquivo);
    });

    /**
     * Manda o arquivo para o backend, que grava no S3, e devolve o objeto no mesmo
     * formato que o InputArquivoS3 do cadastro usa (ArquivoService.obterObjetoTela).
     *
     * Quem fala com o S3 é o servidor: o navegador só faz POST para a própria API.
     * Falha aqui é resposta HTTP do backend, com mensagem — não "Network Error".
     */
    const enviarParaS3 = async (documento) => {
        const arquivo = documento.arquivo;
        const tipo = arquivo.type;

        const conteudo = await lerComoBase64(arquivo);

        let retorno;
        try {
            retorno = await postComProgresso('/faturamento/guiaUploadDocumento', {
                nome_arquivo: removerAcentos(arquivo.name),
                tipo,
                extensao: documento.extensao,
                diretorioS3: DIRETORIO_S3,
                conteudo
            }, (evento) => {
                if (!evento.total) return;
                // Trava em 95% enquanto o servidor grava no S3: o 100% é a resposta.
                atualizarPercentual(documento.id, Math.min(95, Math.round((evento.loaded * 95) / evento.total)));
            });
        } catch (erro) {
            console.error('[extracao-guia] falha no envio (/faturamento/guiaUploadDocumento):', erro);
            throw new Error('Falha ao enviar "' + arquivo.name + '": ' + obterMensagemErro(erro, 'sem resposta do backend'));
        }

        if (!retorno || !retorno.s3Key) {
            console.error('[extracao-guia] backend não devolveu a chave do arquivo:', retorno);
            throw new Error('O backend não confirmou a gravação de "' + arquivo.name + '" no S3.');
        }

        atualizarPercentual(documento.id, 100);

        return {
            name: arquivo.name,
            lastModified: null,
            lastModifiedDate: null,
            webkitRelativePath: '',
            size: arquivo.size,
            type: tipo,
            id: documento.id,
            extension: documento.extensao,
            sizeReadable: tamanhoLegivel(arquivo.size),
            preview: { type: 'file' },
            diretorioS3: DIRETORIO_S3,
            percentualUpload: 100,
            s3Key: retorno.s3Key,
            s3Bucket: retorno.bucket,
            modulo: props.modulo || MODULO['/faturamento/guia'],
            nome_arquivo: arquivo.name,
            tamanho_bytes: arquivo.size,
            tipo: tipo
        };
    };

    const obterMensagemErro = (erro, padrao) => {
        const erros = erro && erro.response && erro.response.data && erro.response.data.erros;
        if (erros && erros.length > 0) return erros.join(' ');
        return (erro && erro.message) || padrao;
    };

    const concluir = (extracao, arquivos) => {
        props.onConcluir && props.onConcluir(extracao || null, arquivos || []);
        limpar();
        props.setAbrirModal(false);
    };

    const continuar = async () => {
        if (documentos.length === 0) {
            mostrarToast('erro', 'Adicione ao menos um arquivo para continuar.');
            return;
        }

        // Arquivo sem nenhuma das duas marcações não tem para onde ir: subir ao
        // bucket só para largá-lo lá é o que se quer evitar. Melhor dizer isso
        // agora do que deixar o envio acontecer à toa.
        const semDestino = documentos.filter(documento => !documento.extrair && !documento.anexar);
        if (semDestino.length > 0) {
            mostrarToast('erro', semDestino.length === 1
                ? `Marque "Extrair" ou "Anexar" para o arquivo "${semDestino[0].arquivo.name}", ou remova-o.`
                : `${semDestino.length} arquivos não estão marcados para extrair nem para anexar. Marque uma das opções ou remova-os.`);
            return;
        }

        setProcessando(true);

        let enviados = [];

        // Envio ao backend: se falhar, nada segue adiante (sem anexo não há o que
        // extrair nem o que anexar na guia).
        try {
            setFase('enviando');

            for (let index = 0; index < documentos.length; index++) {
                const documento = documentos[index];
                const arquivoS3 = await enviarParaS3(documento);
                enviados.push({ documento, arquivoS3 });
            }
        } catch (erro) {
            mostrarToast('erro', obterMensagemErro(erro, 'Falha ao enviar os arquivos.'));
            setProcessando(false);
            setFase('');
            return;
        }

        // As duas marcações são independentes: um arquivo pode ser só lido, só
        // anexado, ou as duas coisas. Quem só é lido não vira anexo da guia;
        // quem só é anexado não passa pela extração.
        const anexos = enviados
            .filter(item => item.documento.anexar)
            .map(item => item.arquivoS3);

        const paraExtracao = enviados
            .filter(item => item.documento.extrair)
            .map(item => ({
                s3Key: item.arquivoS3.s3Key,
                tipo: item.arquivoS3.tipo,
                nome_arquivo: item.arquivoS3.nome_arquivo,
                // Só extrair e não anexar: o backend lê e apaga do bucket. O
                // arquivo precisa subir para ser lido, mas não fica — não vira
                // anexo da guia nem sobra sem ninguém apontando para ele.
                temporario: !item.documento.anexar
            }));

        console.log('[EXTRACAO GUIA] ' + enviados.length + ' arquivo(s) enviado(s) — ' +
            paraExtracao.length + ' para extrair, ' + anexos.length + ' para anexar' +
            (props.particular ? ' (guia particular)' : '') + ': ' +
            enviados.map(item => item.documento.arquivo.name +
                '[' + (item.documento.extrair ? 'extrai' : '-') + '/' + (item.documento.anexar ? 'anexa' : '-') + ']').join(', '));

        // Sem nada marcado para extrair, o usuário quis apenas anexar.
        if (paraExtracao.length === 0) {
            mostrarToast('sucesso', anexos.length === 1 ? 'Arquivo anexado.' : 'Arquivos anexados.');
            concluir(null, anexos);
            return;
        }

        // A leitura pode falhar (documento ilegível, serviço indisponível). Nesse
        // caso os anexos continuam valendo — o usuário digita os campos.
        try {
            setFase('lendo');

            const retorno = await post('/faturamento/guiaExtracaoDocumento', { arquivos: paraExtracao, particular: props.particular === true }, false, true);

            // Ressalvas de uma leitura que deu certo mas merece atenção (OCR
            // degradado, hospital/convênio/paciente não identificado). Um toast
            // por aviso: cada um fala de um campo diferente e pede uma ação
            // diferente na tela — juntos viram um parágrafo que ninguém lê.
            if (retorno && retorno.avisos && retorno.avisos.length > 0)
                retorno.avisos.forEach(aviso => mostrarToast('alerta', aviso, TEMPO_AVISO_MS));

            mostrarToast('sucesso', 'Documento lido. Confira os dados antes de salvar.');
            concluir(retorno && retorno.dados, anexos);
        } catch (erro) {
            console.error('[extracao-guia] falha na extração (/faturamento/guiaExtracaoDocumento):', erro);
            mostrarToast('alerta', 'Leitura do documento indisponível: ' + obterMensagemErro(erro, 'sem resposta do backend') + ' Preencha os dados manualmente.');
            concluir(null, anexos);
        }
    };

    const totalExtrair = documentos.filter(documento => documento.extrair).length;

    const progressoUpload = documentos.length > 0
        ? Math.round(documentos.reduce((soma, documento) => soma + documento.percentual, 0) / documentos.length)
        : 0;
    // Envio ocupa até 85% da barra; a leitura fica em 90% até terminar (como no hub).
    const progresso = fase === 'lendo' ? 90 : Math.round(progressoUpload * 0.85);

    const textoBotao = totalExtrair === 0
        ? (documentos.length > 1 ? 'Anexar ' + documentos.length + ' arquivos' : 'Anexar arquivo')
        : (totalExtrair > 1 ? 'Extrair ' + totalExtrair + ' documentos' : 'Extrair documento');

    return (<>
        <ModalCadastro
            show={props.abrirModal}
            setShowModal={fechar}
            titulo={'Extrair Dados do Documento'}
            tamanho={estiloModal}
        >
            <div className="upload-guia modal-body">
                <p className="text-muted mb-3">
                    Adicione um ou mais arquivos/imagens do comprovante. Os marcados em <b>Extrair</b> são
                    lidos para preencher o cadastro; os marcados em <b>Anexar</b> ficam anexados à guia.
                </p>

                <div
                    className={'upload-guia-dropzone' + (arrastando ? ' arrastando' : '') + (processando ? ' desabilitado' : '')}
                    onDragOver={(evento) => { evento.preventDefault(); if (!processando) setArrastando(true); }}
                    onDragLeave={() => setArrastando(false)}
                    onDrop={soltarArquivos}
                    onClick={() => { if (!processando && inputRef.current) inputRef.current.click(); }}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept={EXTENSOES_ACEITAS}
                        multiple
                        disabled={processando}
                        onChange={selecionarArquivos}
                        onClick={(evento) => evento.stopPropagation()}
                        style={{ display: 'none' }}
                    />
                    <div className="upload-guia-icone-neutro">
                        <span className="icon-Upload"></span>
                    </div>
                    <p className="titulo">Arraste e solte ou clique para selecionar arquivos</p>
                    <p className="detalhe">Formatos suportados: JPG, PNG, WebP, PDF</p>
                    <p className="detalhe">Tamanho máximo: {TAMANHO_MAXIMO_MB}MB por arquivo</p>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={processando}
                        onClick={(evento) => { evento.stopPropagation(); if (inputRef.current) inputRef.current.click(); }}
                    >
                        <span className="icon-Upload mr-2"></span>Selecionar Arquivos
                    </button>
                </div>

                {documentos.length > 0 &&
                    <>
                        <div className="upload-guia-lista-cabecalho">
                            <h6>Documentos ({documentos.length})</h6>
                            <span>Marque o que extrair e/ou anexar</span>
                        </div>
                        <ul className={'upload-guia-lista' + (documentos.length > 5 ? ' rolavel' : '')}>
                            {documentos.map((documento, indice) =>
                                <li className="upload-guia-item" key={documento.id}>
                                    <div
                                        className={'miniatura' + (documento.previewUrl && !processando ? ' editavel' : '')}
                                        title={documento.previewUrl ? 'Editar imagem' : undefined}
                                        onClick={() => abrirEdicao(documento)}
                                    >
                                        {documento.previewUrl ?
                                            <img src={documento.previewUrl} alt={documento.arquivo.name} /> :
                                            <span className={iconePorExtensao(documento.extensao)}></span>}
                                    </div>
                                    <div className="descricao">
                                        <p
                                            className={'nome' + (documento.previewUrl && !processando ? ' editavel' : '')}
                                            title={documento.previewUrl ? 'Editar imagem' : documento.arquivo.name}
                                            onClick={() => abrirEdicao(documento)}
                                        >
                                            {indice + 1}. {documento.arquivo.name}
                                        </p>
                                        <p className="tamanho">
                                            {tamanhoLegivel(documento.arquivo.size)}
                                            {documento.previewUrl && !processando && <span> · clique para editar</span>}
                                        </p>
                                    </div>
                                    <div className="acoes">
                                        {processando &&
                                            <CircularProgressbar
                                                value={documento.percentual}
                                                maxValue={100}
                                                text={documento.percentual + '%'}
                                                background={documento.percentual === 100}
                                                styles={buildStyles({
                                                    backgroundColor: 'transparent',
                                                    pathColor: documento.percentual === 100 ? '4BB543' : '020029',
                                                    textColor: 'black',
                                                })}
                                                className={'progresso-menor' + (documento.percentual === 100 ? ' progresso-menor-sucesso' : '')}
                                            />}
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={documento.extrair}
                                                disabled={processando}
                                                onChange={() => alternarMarcacao(documento.id, 'extrair')}
                                            />
                                            Extrair
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={documento.anexar}
                                                disabled={processando}
                                                onChange={() => alternarMarcacao(documento.id, 'anexar')}
                                            />
                                            Anexar
                                        </label>
                                        <button
                                            type="button"
                                            className="btn-remover"
                                            title="Remover"
                                            disabled={processando}
                                            onClick={() => removerDocumento(documento.id)}
                                        >
                                            &times;
                                        </button>
                                    </div>
                                </li>)}
                        </ul>
                    </>}

                {processando &&
                    <div className="upload-guia-progresso">
                        <div className="legenda">
                            <span>{fase === 'lendo' ? 'Lendo o documento' : 'Enviando arquivos'}</span>
                            <span>{progresso}%</span>
                        </div>
                        <div className="progress">
                            <div
                                className={'progress-bar progress-bar-striped' + (fase === 'lendo' ? ' progress-bar-animated' : '')}
                                role="progressbar"
                                style={{ width: progresso + '%', backgroundColor: '#04022b' }}
                                aria-valuenow={progresso}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                    </div>}

                <div className="upload-guia-acoes">
                    <BotaoPadrao
                        texto={textoBotao}
                        type="button"
                        icone="icon-Upload mr-2"
                        disabled={processando || documentos.length === 0}
                        disabledWithoutText={true}
                        click={continuar}
                        idTooltip="continuar"
                    />
                    <BotaoPadrao
                        texto="Cancelar"
                        type="button"
                        classe="btn btn-outline-secondary"
                        disabled={processando}
                        disabledWithoutText={true}
                        click={fechar}
                        idTooltip="cancelarExtracao"
                    />
                </div>
            </div>
        </ModalCadastro>

        <DocumentScanner
            open={!!documentoEmEdicao}
            sourceFile={documentoEmEdicao ? documentoEmEdicao.original : null}
            onConfirm={confirmarEdicao}
            onClose={fecharEdicao}
        />
    </>);
};

export default ModalExtracaoDocumento;
